import { base44 } from '@/api/base44Client';

export const AVAILABILITY_TEAMS = [
  {
    key: 'worship',
    memberEntity: 'WorshipTeamMember',
    blockEntity: 'AvailabilityBlock',
    memberField: 'worship_member_id'
  },
  {
    key: 'media',
    memberEntity: 'MediaTeamMember',
    blockEntity: 'MediaAvailabilityBlock',
    memberField: 'media_member_id'
  },
  {
    key: 'dance',
    memberEntity: 'DanceTeamMember',
    blockEntity: 'DanceAvailabilityBlock',
    memberField: 'dance_member_id'
  },
  {
    key: 'reception',
    memberEntity: 'ReceptionTeamMember',
    blockEntity: 'ReceptionAvailabilityBlock',
    memberField: 'reception_member_id'
  },
  {
    key: 'kids',
    memberEntity: 'KidsMBVMember',
    blockEntity: 'KidsMBVAvailabilityBlock',
    memberField: 'kids_member_id'
  }
];

const normalizeText = (value) => String(value || '').trim().toLowerCase();
const normalizeName = (value) =>
  normalizeText(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ');
const normalizeDateOnly = (value) => String(value || '').slice(0, 10);

const getMemberName = (member) => member?.full_name || member?.name || member?.member_name || '';
const getMemberEmail = (member) => member?.email || member?.member_email || '';

function hasSameIdentity(left, right) {
  if (!left || !right) return false;

  const leftMemberId = left.member_id || null;
  const rightMemberId = right.member_id || null;
  if (leftMemberId && rightMemberId && String(leftMemberId) === String(rightMemberId)) return true;

  const leftEmail = normalizeText(getMemberEmail(left));
  const rightEmail = normalizeText(getMemberEmail(right));
  if (leftEmail && rightEmail && leftEmail === rightEmail) return true;

  const leftName = normalizeName(getMemberName(left));
  const rightName = normalizeName(getMemberName(right));
  return !!leftName && leftName === rightName;
}

function getBlockMemberName(block, team) {
  return block?.[`${team.key}_member_name`] || block?.worship_member_name || block?.media_member_name || block?.dance_member_name || block?.reception_member_name || block?.kids_member_name || '';
}

function getBlocksByTeam(blockRows) {
  return AVAILABILITY_TEAMS.reduce((acc, team) => {
    acc[team.key] = blockRows?.[team.key] || [];
    return acc;
  }, {});
}

function getMemberLookup(memberRows) {
  return AVAILABILITY_TEAMS.reduce((acc, team) => {
    acc[team.key] = new Map((memberRows?.[team.key] || []).map((member) => [String(member.id), member]));
    return acc;
  }, {});
}

export async function fetchGlobalAvailabilityData() {
  const [blockEntries, memberEntries] = await Promise.all([
    Promise.all(
      AVAILABILITY_TEAMS.map(async (team) => [
        team.key,
        await base44.entities[team.blockEntity].list('-blocked_date', 2000)
      ])
    ),
    Promise.all(
      AVAILABILITY_TEAMS.map(async (team) => [
        team.key,
        await base44.entities[team.memberEntity].list('-created_date', 2000)
      ])
    )
  ]);

  const blockRows = Object.fromEntries(blockEntries);
  const memberRows = Object.fromEntries(memberEntries);

  return {
    blocksByTeam: getBlocksByTeam(blockRows),
    membersByTeam: memberRows,
    memberLookup: getMemberLookup(memberRows)
  };
}

export function findGlobalAvailabilityBlock(member, blockedDate, globalAvailabilityData) {
  const normalizedDate = normalizeDateOnly(blockedDate);
  if (!member || !normalizedDate || !globalAvailabilityData) return null;

  for (const team of AVAILABILITY_TEAMS) {
    const blocks = globalAvailabilityData.blocksByTeam?.[team.key] || [];
    const memberLookup = globalAvailabilityData.memberLookup?.[team.key] || new Map();

    const block = blocks.find((candidate) => {
      if (normalizeDateOnly(candidate.blocked_date) !== normalizedDate) return false;

      const blockedTeamMemberId = candidate[team.memberField];
      if (blockedTeamMemberId && member.id && String(blockedTeamMemberId) === String(member.id)) return true;

      const blockedTeamMember = blockedTeamMemberId ? memberLookup.get(String(blockedTeamMemberId)) : null;
      if (blockedTeamMember && hasSameIdentity(member, blockedTeamMember)) return true;

      const createdByEmail = normalizeText(candidate.created_by_email);
      const memberEmail = normalizeText(getMemberEmail(member));
      if (createdByEmail && memberEmail && createdByEmail === memberEmail) return true;

      const blockMemberName = normalizeName(getBlockMemberName(candidate, team));
      const memberName = normalizeName(getMemberName(member));
      if (blockMemberName && memberName && blockMemberName === memberName) return true;

      return false;
    });

    if (block) return { ...block, team: team.key };
  }

  return null;
}

export function listGlobalAvailabilityBlocksForMember(member, globalAvailabilityData) {
  if (!member || !globalAvailabilityData) return [];

  const matches = [];

  for (const team of AVAILABILITY_TEAMS) {
    const blocks = globalAvailabilityData.blocksByTeam?.[team.key] || [];
    const memberLookup = globalAvailabilityData.memberLookup?.[team.key] || new Map();

    blocks.forEach((block) => {
      const teamMemberId = block?.[team.memberField];
      const teamMember = teamMemberId ? memberLookup.get(String(teamMemberId)) : null;
      const blockMemberName = getBlockMemberName(block, team);

      if (
        (teamMember && hasSameIdentity(member, teamMember)) ||
        (teamMemberId && member.id && String(teamMemberId) === String(member.id)) ||
        (normalizeText(block.created_by_email) && normalizeText(getMemberEmail(member)) && normalizeText(block.created_by_email) === normalizeText(getMemberEmail(member))) ||
        (normalizeName(blockMemberName) && normalizeName(getMemberName(member)) && normalizeName(blockMemberName) === normalizeName(getMemberName(member)))
      ) {
        matches.push({ ...block, team: team.key });
      }
    });
  }

  const byDate = new Map();
  matches.forEach((block) => {
    const date = normalizeDateOnly(block.blocked_date);
    if (date && !byDate.has(date)) byDate.set(date, { ...block, blocked_date: date });
  });

  return Array.from(byDate.values()).sort((a, b) => String(b.blocked_date || '').localeCompare(String(a.blocked_date || '')));
}
